import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Search, Filter, MapPin, QrCode, AlertTriangle, 
  CheckCircle, Settings, RefreshCw, Eye
} from 'lucide-react';

const PortalEquipmentPage = () => {
  const { user } = useAuth();
  const { selectedPlant, selectedLocation, locations } = useData();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchEquipment();
  }, [selectedPlant, selectedLocation]);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const response = await api.get('/equipment');
      let data = response.data || [];
      
      // Filter by user's plant/location access
      if (user?.plant_access?.length > 0) {
        data = data.filter(e => user.plant_access.includes(e.plant_id));
      }
      if (user?.location_access?.length > 0) {
        data = data.filter(e => user.location_access.includes(e.location_id));
      }
      
      // Apply selected filters
      if (selectedPlant && selectedPlant !== 'all') {
        data = data.filter(e => e.plant_id === selectedPlant);
      }
      if (selectedLocation && selectedLocation !== 'all') {
        data = data.filter(e => e.location_id === selectedLocation);
      }
      
      setEquipment(data);
    } catch (error) {
      toast.error('Eroare la incarcarea echipamentelor');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      operational: 'bg-green-100 text-green-700 border-green-200',
      maintenance: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      broken: 'bg-red-100 text-red-700 border-red-200',
      inactive: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-600';
  };

  const getStatusLabel = (status) => {
    const labels = {
      operational: 'Operational',
      maintenance: 'In Mentenanta',
      broken: 'Defect',
      inactive: 'Inactiv'
    };
    return labels[status] || status;
  };

  const filteredEquipment = equipment.filter(e => {
    const matchesSearch = !searchTerm || 
      e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.location_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || e.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Group by location for better visualization
  const groupedEquipment = filteredEquipment.reduce((acc, eq) => {
    const loc = eq.location_name || 'Fara locatie';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(eq);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 pb-20 lg:pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Echipamente Sectie</h1>
          <p className="text-slate-500">{filteredEquipment.length} echipamente</p>
        </div>
        <Button onClick={fetchEquipment} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Reimprospateaza
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cauta echipament..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate</SelectItem>
                <SelectItem value="operational">Operational</SelectItem>
                <SelectItem value="maintenance">In Mentenanta</SelectItem>
                <SelectItem value="broken">Defect</SelectItem>
                <SelectItem value="inactive">Inactiv</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Equipment List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-500">Se incarca...</p>
        </div>
      ) : Object.keys(groupedEquipment).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Settings className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900">Niciun echipament gasit</h3>
            <p className="text-slate-500">Modifica filtrele pentru a vedea mai multe rezultate</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedEquipment).map(([location, items]) => (
            <div key={location}>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-3">
                <MapPin className="w-5 h-5 text-blue-600" />
                {location}
                <Badge variant="outline" className="ml-2">{items.length}</Badge>
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map(eq => (
                  <Card key={eq.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <Badge variant="outline" className={getStatusColor(eq.status)}>
                          {getStatusLabel(eq.status)}
                        </Badge>
                        <QrCode className="w-5 h-5 text-slate-400" />
                      </div>
                      <h3 className="font-medium text-slate-900 mb-1">{eq.name}</h3>
                      <p className="text-sm text-slate-500 mb-2">Cod: {eq.code}</p>
                      {eq.category_name && (
                        <p className="text-xs text-slate-400">{eq.category_name}</p>
                      )}
                      {eq.active_tickets_count > 0 && (
                        <div className="flex items-center gap-1 mt-2 text-sm text-orange-600">
                          <AlertTriangle className="w-4 h-4" />
                          {eq.active_tickets_count} tichete active
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalEquipmentPage;
